import React, { useState, useEffect } from 'react';
import { Button, Form, Icon, Dropdown, Modal, Message, Header, Input, Loader, Popup, Confirm } from 'semantic-ui-react';
import { discoverMcpTools, deleteMcpToolAPI } from '../../apis';
import '../../styles/MCPToolsManager.css';

/**
 * MCPToolsManager component for managing MCP tools per dataset.
 * This component allows users to create, view, edit, and delete MCP tools.
 * Tools are only visible when Agentic Instruction is enabled.
 */
const MCPToolsManager = ({ 
  mcpTools = [], 
  onToolsChange, 
  isEnabled = false,
  isReadOnly = false,
  datasetId,
  getClassName,
  location
}) => {
  const [tools, setTools] = useState(mcpTools || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveredTools, setDiscoveredTools] = useState([]);
  const [isDiscovered, setIsDiscovered] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);
  // Store original header values for saving (plain text, not masked)
  const [originalHeaders, setOriginalHeaders] = useState([]);

  // Form state for adding/editing tool
  const [toolForm, setToolForm] = useState({
    label: '',
    mcpUrl: '',
    headers: [{ name: '', value: '' }],
    connectionType: 'streamable_http',
    headerInUrl: false,
  });

  // Update local state when props change
  useEffect(() => {
    setTools(mcpTools || []);
  }, [mcpTools]);

  // Initialize selected tools when tools change (all selected by default)
  useEffect(() => {
    if (tools.length > 0 && selectedTools.length === 0) {
      setSelectedTools(tools.map(tool => tool.toolId || tool.id));
    }
  }, [tools]);

  // Connection type options
  const connectionTypeOptions = [
    { key: 'sse', text: 'SSE (Server-Sent Events)', value: 'sse' },
    { key: 'streamable_http', text: 'Streamable HTTP', value: 'streamable_http' },
  ];

  // Handle opening modal for adding new tool
  const handleAddTool = () => {
    setEditingTool(null);
    setToolForm({
      label: '',
      mcpUrl: '',
      headers: [{ name: '', value: '' }],
      connectionType: 'streamable_http',
      headerInUrl: false,
    });
    setOriginalHeaders([]);
    setError('');
    setDiscoveredTools([]);
    setIsDiscovered(false);
    setServerInfo(null);
    setIsModalOpen(true);
  };

  // Handle opening modal for editing existing tool
  const handleEditTool = (tool) => {
    setEditingTool(tool);
    const existingHeaders = tool.headers && tool.headers.length > 0 
      ? tool.headers.map(h => ({ name: h.name || '', value: h.value || '' }))
      : [{ name: '', value: '' }];
    setToolForm({
      label: tool.label || '',
      mcpUrl: tool.mcpUrl || tool.mcp_url || '',
      headers: existingHeaders,
      connectionType: tool.connectionType || tool.protocol || 'streamable_http',
      headerInUrl: tool.headerInUrl || false,
    });
    // Store original headers for tracking changes
    // If value is masked (********), keep it to detect if user changed it
    setOriginalHeaders(tool.originalHeaders || existingHeaders);
    setError('');
    // For editing, show existing discovered tools
    setDiscoveredTools(tool.discoveredTools || []);
    setIsDiscovered(tool.discoveredTools && tool.discoveredTools.length > 0);
    setServerInfo(tool.serverInfo || null);
    setIsModalOpen(true);
  };

  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle delete confirmation
  const handleDeleteClick = (tool) => {
    setToolToDelete(tool);
    setDeleteConfirmOpen(true);
  };

  // Handle deleting a tool
  const handleDeleteTool = async () => {
    if (!toolToDelete) return;
    
    const toolId = toolToDelete.toolId || toolToDelete.id;
    setIsDeleting(true);
    
    try {
      // Call delete API if datasetId exists (tool is saved in database)
      if (datasetId && toolToDelete._id) {
        await deleteMcpToolAPI(datasetId, toolId);
      }
      
      // Update local state
      const updatedTools = tools.filter(t => (t.toolId || t.id) !== toolId);
      setTools(updatedTools);
      setSelectedTools(selectedTools.filter(id => id !== toolId));
      if (typeof onToolsChange === 'function') {
        onToolsChange(updatedTools, selectedTools.filter(id => id !== toolId));
      }
    } catch (err) {
      console.error('Error deleting MCP tool:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete MCP tool');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setToolToDelete(null);
    }
  };

  // Handle adding a new header field
  const handleAddHeader = () => {
    setToolForm({
      ...toolForm,
      headers: [...toolForm.headers, { name: '', value: '' }],
    });
    setOriginalHeaders([...originalHeaders, { name: '', value: '' }]);
  };

  // Handle removing a header field
  const handleRemoveHeader = (index) => {
    const newHeaders = toolForm.headers.filter((_, i) => i !== index);
    const newOriginalHeaders = originalHeaders.filter((_, i) => i !== index);
    setToolForm({
      ...toolForm,
      headers: newHeaders.length > 0 ? newHeaders : [{ name: '', value: '' }],
    });
    setOriginalHeaders(newOriginalHeaders.length > 0 ? newOriginalHeaders : [{ name: '', value: '' }]);
  };

  // Handle header field change
  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...toolForm.headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setToolForm({ ...toolForm, headers: newHeaders });
    
    // Also update originalHeaders when user types a new value
    // This ensures the actual value is saved, not masked value
    const newOriginalHeaders = [...originalHeaders];
    if (newOriginalHeaders[index]) {
      newOriginalHeaders[index] = { ...newOriginalHeaders[index], [field]: value };
    } else {
      newOriginalHeaders[index] = { name: '', value: '', [field]: value };
    }
    setOriginalHeaders(newOriginalHeaders);
  };

  // Handle form field change
  const handleFormChange = (field, value) => {
    setToolForm({ ...toolForm, [field]: value });
  };

  // Validate form for discovery (only URL required)
  const validateForDiscovery = () => {
    if (!toolForm.mcpUrl.trim()) {
      setError('MCP URL is required');
      return false;
    }
    try {
      new URL(toolForm.mcpUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return false;
    }
    setError('');
    return true;
  };

  // Validate form for save (label + discovery required)
  const validateForm = () => {
    if (!toolForm.label.trim()) {
      setError('Tool name (Label) is required');
      return false;
    }
    if (!toolForm.mcpUrl.trim()) {
      setError('MCP URL is required');
      return false;
    }
    try {
      new URL(toolForm.mcpUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return false;
    }
    if (!isDiscovered || discoveredTools.length === 0) {
      setError('Please discover tools first before saving');
      return false;
    }
    setError('');
    return true;
  };

  // Handle discovering tools from MCP server
  const handleDiscoverTools = async () => {
    if (!validateForDiscovery()) return;

    setIsDiscovering(true);
    setError('');
    setDiscoveredTools([]);
    setIsDiscovered(false);
    setServerInfo(null);

    try {
      const filteredHeaders = toolForm.headers.filter(h => h.name.trim() || h.value.trim());
      
      // Store original headers before sending to backend (plain text values for saving)
      setOriginalHeaders(filteredHeaders.map(h => ({ ...h })));
      
      const payload = {
        mcpUrl: toolForm.mcpUrl.trim(),
        headers: filteredHeaders,
        connectionType: toolForm.connectionType,
        headerInUrl: toolForm.headerInUrl,
      };

      const response = await discoverMcpTools(payload);

      if (response.success) {
        setDiscoveredTools(response.tools || []);
        setServerInfo(response.serverInfo || null);
        setIsDiscovered(true);
        
        // Auto-fill label from server name if empty
        if (!toolForm.label.trim() && response.serverInfo?.name) {
          setToolForm(prev => ({ ...prev, label: response.serverInfo.name }));
        }
        
        // NOTE: Do NOT replace headers with masked values!
        // Keep original headers in originalHeaders state for saving
        // The form still shows the password field which is masked by input type="password"
      } else {
        setError(response.error || 'Failed to discover tools from MCP server');
      }
    } catch (err) {
      console.error('Error discovering MCP tools:', err);
      setError(err.response?.data?.error || err.message || 'Failed to connect to MCP server');
    } finally {
      setIsDiscovering(false);
    }
  };

  // Handle saving tool (add or update)
  const handleSaveTool = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Use originalHeaders (plain text) for saving to database
      // originalHeaders contains the actual values entered by user
      const headersToSave = originalHeaders.length > 0 
        ? originalHeaders.filter(h => h.name.trim() || h.value.trim())
        : toolForm.headers.filter(h => h.name.trim() || h.value.trim());
      
      // Create masked version for display in UI
      const maskedHeaders = headersToSave.map(h => ({
        name: h.name,
        value: h.value ? '********' : ''
      }));
      
      const toolData = {
        toolId: editingTool?.toolId || editingTool?.id || `tool_${Date.now()}`,
        label: toolForm.label.trim(),
        mcpUrl: toolForm.mcpUrl.trim(),
        mcp_url: toolForm.mcpUrl.trim(),
        // Send original (plain text) headers to backend for storage
        headers: headersToSave,
        // Store masked headers for display in UI (optional, backend will mask anyway)
        displayHeaders: maskedHeaders,
        // Keep original headers reference for future edits
        originalHeaders: headersToSave,
        connectionType: toolForm.connectionType,
        protocol: toolForm.connectionType,
        headerInUrl: toolForm.headerInUrl,
        datasetId: datasetId,
        discoveredTools: discoveredTools,
        serverInfo: serverInfo,
        createdAt: editingTool?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      let updatedTools;
      if (editingTool) {
        // Update existing tool
        updatedTools = tools.map(t => 
          (t.toolId || t.id) === (editingTool.toolId || editingTool.id) 
            ? toolData 
            : t
        );
      } else {
        // Add new tool
        updatedTools = [...tools, toolData];
        // Add new tool to selected tools by default
        setSelectedTools([...selectedTools, toolData.toolId]);
      }

      setTools(updatedTools);
      if (typeof onToolsChange === 'function') {
        const newSelectedTools = editingTool 
          ? selectedTools 
          : [...selectedTools, toolData.toolId];
        onToolsChange(updatedTools, newSelectedTools);
      }

      setIsModalOpen(false);
      setEditingTool(null);
    } catch (err) {
      setError('Failed to save tool. Please try again.');
      console.error('Error saving MCP tool:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle tool selection change
  const handleToolSelectionChange = (e, { value }) => {
    setSelectedTools(value);
    if (typeof onToolsChange === 'function') {
      onToolsChange(tools, value);
    }
  };

  // Handle removing a selected tool
  const handleRemoveSelectedTool = (toolId) => {
    const newSelectedTools = selectedTools.filter(id => id !== toolId);
    setSelectedTools(newSelectedTools);
    if (typeof onToolsChange === 'function') {
      onToolsChange(tools, newSelectedTools);
    }
  };

  // Get tool options for dropdown (only unselected tools)
  const getToolOptions = () => {
    return tools
      .filter(tool => !selectedTools.includes(tool.toolId || tool.id))
      .map(tool => ({
        key: tool.toolId || tool.id,
        text: tool.label,
        value: tool.toolId || tool.id,
      }));
  };

  // Get selected tool objects
  const getSelectedToolObjects = () => {
    return tools.filter(tool => selectedTools.includes(tool.toolId || tool.id));
  };

  // Mask header value for display (show asterisks)
  const maskHeaderValue = (value) => {
    if (!value) return '';
    if (value.startsWith('KV:') || value.includes('*')) return value;
    return '********';
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <div className={`mcp-tools-manager ${getClassName ? getClassName('mcp-tools-container', location) : ''}`}>
      <Form.Field>
        <div className="mcp-tools-header">
          <label className={getClassName ? getClassName('prompt-label', location) : 'mcp-tools-label'}>
            MCP Tools
          </label>
          {!isReadOnly && (
            <Button 
              size="small" 
              className="add-tool-button"
              onClick={handleAddTool}
            >
              + Add Tool
            </Button>
          )}
        </div>

        {/* Tool Selection Dropdown */}
        {tools.length > 0 && (
          <div className="tool-selection-container">
            <label className="tool-selection-label">Active Tools:</label>
            
            {/* Selected Tools Tags */}
            {selectedTools.length > 0 && (
              <div className="selected-tools-tags">
                {getSelectedToolObjects().map((tool) => (
                  <div key={tool.toolId || tool.id} className="selected-tool-tag">
                    <span className="tag-text">{tool.label}</span>
                    {!isReadOnly && (
                      <img
                        src="/images/grey-circle-close.svg"
                        alt="Remove"
                        className="tag-remove"
                        onClick={() => handleRemoveSelectedTool(tool.toolId || tool.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Dropdown to add more tools */}
            {!isReadOnly && (
              <Dropdown
                placeholder={getToolOptions().length > 0 ? "Add tools..." : "All tools selected"}
                fluid
                selection
                options={getToolOptions()}
                onChange={(e, { value }) => {
                  if (value) {
                    const newSelectedTools = [...selectedTools, value];
                    setSelectedTools(newSelectedTools);
                    if (typeof onToolsChange === 'function') {
                      onToolsChange(tools, newSelectedTools);
                    }
                  }
                }}
                value=""
                disabled={getToolOptions().length === 0}
                className="tool-selection-dropdown"
              />
            )}
          </div>
        )}

        {/* Tool List */}
        <div className="mcp-tools-list">
          {tools.length === 0 ? (
            <div className="no-tools-message">
              No MCP tools configured. Click "Add Tool" to add your first tool.
            </div>
          ) : (
            tools.map((tool, index) => (
              <div key={tool.toolId || tool.id || index} className="mcp-tool-card">
                <div className="tool-card-header">
                  <span className="tool-name">{tool.label}</span>
                  <span className={`tool-protocol ${tool.connectionType || tool.protocol}`}>
                    {(tool.connectionType || tool.protocol || 'streamable_http').toUpperCase()}
                  </span>
                </div>
                <div className="tool-card-url">
                  <span>{tool.mcpUrl || tool.mcp_url}</span>
                </div>
                {tool.discoveredTools && tool.discoveredTools.length > 0 && (
                  <div className="tool-card-discovered">
                    <Icon name="wrench" size="small" />
                    <span>{tool.discoveredTools.length} tool(s): {tool.discoveredTools.map(t => t.name).join(', ')}</span>
                  </div>
                )}
                {tool.headers && tool.headers.length > 0 && (
                  <div className="tool-card-headers">
                    <span>{tool.headers.length} header(s) configured</span>
                  </div>
                )}
                {!isReadOnly && (
                  <div className="tool-card-actions">
                    <div className="tool-card-icons grey-icons">
                      <Popup
                        content="Edit tool"
                        inverted
                        position="top center"
                        style={{ borderRadius: "10px" }}
                        trigger={
                          <img
                            className="tableButton"
                            src="/images/Edit.svg"
                            alt="Edit"
                            onClick={() => handleEditTool(tool)}
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                          />
                        }
                      />
                      <Popup
                        content="Delete tool"
                        inverted
                        position="top center"
                        style={{ borderRadius: "10px" }}
                        trigger={
                          <img
                            className="tableButton"
                            src="/images/grey-delete.svg"
                            alt="Delete"
                            onClick={() => handleDeleteClick(tool)}
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                          />
                        }
                      />
                    </div>
                    <div className="tool-card-icons color-icons">
                      <Popup
                        content="Edit tool"
                        inverted
                        position="top center"
                        style={{ borderRadius: "10px" }}
                        trigger={
                          <img
                            className="tableButton"
                            src="/images/blue-pen.svg"
                            alt="Edit"
                            onClick={() => handleEditTool(tool)}
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                          />
                        }
                      />
                      <Popup
                        content="Delete tool"
                        inverted
                        position="top center"
                        style={{ borderRadius: "10px" }}
                        trigger={
                          <img
                            className="tableButton"
                            src="/images/red-delete.svg"
                            alt="Delete"
                            onClick={() => handleDeleteClick(tool)}
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                          />
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Form.Field>

      {/* Add/Edit Tool Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="tiny"
        className="add-document-model"
      >
        <Modal.Content>
          <Header as="h3">
            {editingTool ? 'Edit MCP Server' : 'Connect to MCP Server'}
          </Header>
          
          {error && (
            <Message negative>
              <p>{error}</p>
            </Message>
          )}
          
          <label>Label</label>
          <br />
          <Input
            type="text"
            placeholder="MCP Server Label"
            value={toolForm.label}
            onChange={(e) => handleFormChange('label', e.target.value)}
            className="add-datset-input"
          />
          <br />
          <br />
          <p className="add-dataset-description">Connection Type</p>
          <div className="mcp-radio-group">
            <label className="mcp-radio-label">
              <input
                type="radio"
                name="connectionType"
                value="sse"
                checked={toolForm.connectionType === 'sse'}
                onChange={() => handleFormChange('connectionType', 'sse')}
              />
              <span>SSE</span>
            </label>
            <label className="mcp-radio-label">
              <input
                type="radio"
                name="connectionType"
                value="streamable_http"
                checked={toolForm.connectionType === 'streamable_http'}
                onChange={() => handleFormChange('connectionType', 'streamable_http')}
              />
              <span>Streamable HTTP</span>
            </label>
          </div>
          <br />
          <label>MCP URL</label>
          <br />
          <Input
            type="text"
            placeholder="https://your-mcp-server.com/mcp"
            value={toolForm.mcpUrl}
            onChange={(e) => handleFormChange('mcpUrl', e.target.value)}
            className="add-datset-input"
          />
          <br />

          <label className="mcp-checkbox-label">
            <input
              type="checkbox"
              id="headerInUrl"
              checked={toolForm.headerInUrl}
              onChange={(e) => handleFormChange('headerInUrl', e.target.checked)}
            />
            Headers embedded in URL
          </label>
          <br />

          {!toolForm.headerInUrl && (
            <>
              <p className="add-dataset-description">Authentication</p>
              <div className="mcp-headers-container">
                {toolForm.headers.map((header, index) => (
                  <div key={index} className="mcp-header-row">
                    <Input
                      placeholder="Header"
                      value={header.name}
                      onChange={(e) => handleHeaderChange(index, 'name', e.target.value)}
                      className="add-datset-input mcp-header-name"
                    />
                    <Input
                      placeholder="Header value"
                      type="password"
                      value={header.value}
                      onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                      className="add-datset-input mcp-header-value"
                    />
                    {toolForm.headers.length > 1 && (
                      <button
                        type="button"
                        className="mcp-header-btn mcp-remove-btn"
                        onClick={() => handleRemoveHeader(index)}
                      >
                        −
                      </button>
                    )}
                    {index === toolForm.headers.length - 1 && (
                      <button
                        type="button"
                        className="mcp-header-btn mcp-add-btn"
                        onClick={handleAddHeader}
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <br />
            </>
          )}

          {/* Discover Tools Button */}
          <div className="discover-tools-section">
            <button
              type="button"
              className="discover-btn"
              onClick={handleDiscoverTools}
              disabled={isDiscovering || !toolForm.mcpUrl.trim()}
            >
              {isDiscovering ? (
                <>
                  <Loader active inline size="tiny" /> Discovering...
                </>
              ) : (
                'Discover Tools'
              )}
            </button>
          </div>

          {/* Discovered Tools Display */}
          {isDiscovered && discoveredTools.length > 0 && (
            <div className="discovered-tools-container">
              <Message positive>
                <Message.Header>
                  <Icon name="check circle" />
                  {serverInfo?.name ? `Connected to: ${serverInfo.name}` : 'MCP Server Connected'}
                  {serverInfo?.version && <span className="server-version"> v{serverInfo.version}</span>}
                </Message.Header>
                <p>{discoveredTools.length} tool(s) discovered</p>
              </Message>
              <div className="discovered-tools-list">
                {discoveredTools.map((tool, index) => (
                  <div key={index} className="discovered-tool-item">
                    <div className="discovered-tool-name">
                      <Icon name="wrench" size="small" />
                      {tool.name}
                    </div>
                    {tool.description && (
                      <div className="discovered-tool-description">
                        {tool.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isDiscovered && discoveredTools.length === 0 && (
            <Message warning>
              <Message.Header>No Tools Found</Message.Header>
              <p>The MCP server connected successfully but no tools were discovered.</p>
            </Message>
          )}
        </Modal.Content>

        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={() => setIsModalOpen(false)}
            disabled={isLoading || isDiscovering}
          >
            Cancel
          </button>
          <button
            className="add-btn"
            onClick={handleSaveTool}
            disabled={isLoading || isDiscovering || !isDiscovered || discoveredTools.length === 0}
            title={!isDiscovered ? 'Discover tools first' : ''}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Confirm
        open={deleteConfirmOpen}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setToolToDelete(null);
        }}
        onConfirm={handleDeleteTool}
        header="Delete MCP Tool"
        content={`Are you sure you want to delete "${toolToDelete?.label || 'this tool'}"? This action cannot be undone.`}
        confirmButton={isDeleting ? 'Deleting...' : 'Delete'}
        cancelButton="Cancel"
        size="tiny"
      />
    </div>
  );
};

export default MCPToolsManager;
